import type { ServerError, ServerParseError } from "@apollo/client";
import { createHttpLink, ApolloLink, makeVar } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import type { GraphQLError } from "graphql";
import { logger } from "~/lib/logger";

export const profileIdVar = makeVar("");
export const graphQLErrorsVar = makeVar<readonly GraphQLError[] | undefined>(undefined);
export const networkErrorVar = makeVar<Error | ServerError | ServerParseError | undefined>(undefined);

/**
 * NOTE: Botの場合は、BFFのメンテナンス状態をスキップするためにBFFチームから指定されたトークンを`X-Dmm-Ignore-Maintenance` フィールドに設定する
 * 通常ユーザー時には空文字列を指定する。
 * @see {@link https://tsuchinoko.slack.com/archives/C02B3BET8UB/p1692863986099969?thread_ts=1692859547.631909&cid=C02B3BET8UB|Slack}
 */
const headerForIgnoreMaintenance = { "X-Dmm-Ignore-Maintenance": "" };

const fetcher = (input: RequestInfo | URL, init?: RequestInit) => {
  return fetch(input, init);
};

/**
 * Apollo client の通信に関する設定
 */
export const httpLink = createHttpLink({
  uri: `${process.env.NEXT_PUBLIC_GQL_ENDPOINT}/graphql`,
  // TODO(uchida): 環境毎にcredentialsの設定を変更する
  credentials: "include",
  fetch: (input, init) => {
    return fetcher(input, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        "x-dmm-profile-id": profileIdVar(),
        ...headerForIgnoreMaintenance,
      },
    });
  },
});

/**
 * Apollo client のエラーハンドリングに関する設定
 */
export const errorLink = onError(({ graphQLErrors, networkError, operation: { operationName } }) => {
  /**
   * graphQL errorを回避し
   * 独自にerror handlingを実施したい場合に
   * 対象のoperationNameを追記する
   * */
  const isSkipOperations =
    operationName === "GetVideoByMiniDetail" ||
    operationName === "GetVideoContentByMiniDetail" ||
    operationName === "GetShelves" ||
    operationName === "PostVideoReview" ||
    operationName === "PostComment";

  if (graphQLErrors) {
    if (isSkipOperations) return;
    graphQLErrorsVar(graphQLErrors);
    logger.error({
      networkError,
    });
  }

  if (networkError) {
    networkErrorVar(networkError);
    logger.error({
      networkError,
    });
  }
});

/**
 * レスポンスをハンドリングする処理
 */
export const responseLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((res) => {
    logger.info(res, `res: ${operation.operationName}`);

    return res;
  });
});
